import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { formatWeight, type RegionId } from './region'

// ---------------------------------------------------------------------------
// Vitals source — who recorded the measurement
// ---------------------------------------------------------------------------

export const vitalsSourceSchema = z.enum(['clinical', 'member'])
export type VitalsSource = z.infer<typeof vitalsSourceSchema>

// ---------------------------------------------------------------------------
// Base types
// ---------------------------------------------------------------------------

/** Write-side vitals shape — numeric values before string conversion for storage. */
export interface VitalsInput {
  /** Weight in kg. */
  weight?: number
  /** Temperature in °C. */
  temperature?: number
  /** Heart rate in bpm. */
  heartRate?: number
  /** Respiratory rate in breaths per minute. */
  respiratoryRate?: number
  /** Body condition score, 1–9 scale. */
  bodyConditionScore?: number
  /** Pain score, 0–10 scale. */
  painScore?: number
}

export const vitalsSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  consultationId: z.uuid().nullable(),
  /** Null when member-recorded. */
  staffId: z.uuid().nullable(),
  source: vitalsSourceSchema,
  /** Weight in kg. */
  weight: z.string().nullable(),
  /** Temperature in °C. */
  temperature: z.string().nullable(),
  /** Heart rate in bpm. */
  heartRate: z.number().int().nullable(),
  /** Respiratory rate in breaths per minute. */
  respiratoryRate: z.number().int().nullable(),
  /** Body condition score, 1–9 scale. */
  bodyConditionScore: z.number().nullable(),
  /** Pain score, 0–10 scale. */
  painScore: z.number().int().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Vitals = z.output<typeof vitalsSchema>

/**
 * Derives the display-ready latest weight from a vitals history list (ordered
 * newest-first, as the vitals endpoint returns it). Scans for the most recent
 * entry that actually recorded a weight — a newer entry that logged only, say,
 * a temperature doesn't mask an older weight. Returns null when no entry has a
 * weight on file.
 */
export function formatLatestWeight(
  vitals: { weight: string | null }[] | undefined,
  regionId: RegionId
): string | null {
  const weight = vitals?.find((v) => v.weight)?.weight
  return weight ? formatWeight(Number.parseFloat(weight), regionId) : null
}
